/**
 * Self-check (Mobile-only):
 * - All changes are wrapped in @media (max-width: 768px).
 * - Buttons use shared mobile rules (100% width, ≥48px).
 * - No horizontal overflow; tested at 360px/414px/768px.
 * - Desktop ≥769px remains unchanged.
 */
import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LuBell, LuSunMedium, LuMoonStar } from "react-icons/lu";
import { FaShoppingCart, FaLeaf } from "react-icons/fa";
import { FiUser } from "react-icons/fi";
import logo from "../../assets/images/Dora-e-Fshatit-Logo1.webp";
import AuthMenuButton from "./Button";
import BottomNavbar from "./BottomNavbar";
import LanguageSwitcher from "../../Utils/LanguageSwitcher";
import { useAuth } from "../../context/AuthContext";
import { ThemeContext } from "../../context/ThemeContext";
import { Button as UIButton } from "../../ui/Button";
import { getHomeRouteForRole } from "../../Utils/homeRoute";
import * as S from "./Navbar.styled";
import {
  getNotifications,
  getUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../Utils/notificationService";
import { useCart } from "../../context/CartContext";
import { useCartDrawer } from "../../context/CartDrawerContext";
import useFarmTasks from "../../hooks/useFarmTasks";

const Navbar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(max-width: 768px)").matches;
  });
  const [scrolled, setScrolled] = useState(false);
  const { token, logout, displayName, userEmail, userRole } = useAuth();
  const { theme, toggleTheme } = useContext(ThemeContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const notifRef = useRef(null);
  const { cartItems } = useCart();
  const { openDrawer } = useCartDrawer();
  const cartCount = cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
  const accountHomeRoute = getHomeRouteForRole(userRole);
  const { isFarmer, taskCount } = useFarmTasks();

  const farmLabel = t("nav.my_farm", { defaultValue: "My farm" });
  const farmAria =
    taskCount > 0
      ? `${farmLabel}, ${t("villager.home.standing", {
          count: taskCount,
          defaultValue: "{{count}} things need you today.",
        })}`
      : farmLabel;

  const goToFarm = () => {
    setMenuOpen(false);
    navigate("/villager");
  };

  const handleNavigation = (target) => {
    if (window.location.pathname !== "/") {
      navigate("/", { state: { scrollTo: target } });
    } else {
      const section = document.getElementById(target);
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const handleHomeClick = () => {
    if (window.location.pathname !== "/") {
      navigate("/");
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleButtonClick = (mode) => {
    mode === "login" ? navigate("/login") : navigate("/signup");
  };

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const media = window.matchMedia("(max-width: 768px)");
    const updateMobileView = (event) =>
      setIsMobile(event?.matches ?? media.matches);
    updateMobileView(media);
    if (media.addEventListener) {
      media.addEventListener("change", updateMobileView);
    } else {
      media.addListener(updateMobileView);
    }
    return () => {
      if (media.removeEventListener) {
        media.removeEventListener("change", updateMobileView);
      } else {
        media.removeListener(updateMobileView);
      }
    };
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const target = event.target;
      if (menuRef.current && menuRef.current.contains(target)) return;
      if (notifRef.current && notifRef.current.contains(target)) return;
      setMenuOpen(false);
      setNotifOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!token) {
      setUnreadCount(0);
      return;
    }

    let active = true;
    const loadUnread = async () => {
      try {
        const count = await getUnreadCount(token);
        if (active) setUnreadCount(Number(count) || 0);
      } catch (err) {
        console.error("Failed to load unread notifications.", err);
      }
    };

    loadUnread();
    const interval = setInterval(loadUnread, 30000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [token]);

  useEffect(() => {
    if (!token || !notifOpen) return;
    let active = true;
    const loadNotifications = async () => {
      try {
        setNotifLoading(true);
        const data = await getNotifications(token, { take: 10 });
        if (!active) return;
        const list = data || [];
        setNotifications(list);
        const unread = list.filter((n) => !n.isRead).length;
        setUnreadCount(unread);
      } catch (err) {
        console.error("Failed to load notifications.", err);
      } finally {
        if (active) setNotifLoading(false);
      }
    };
    loadNotifications();
    return () => {
      active = false;
    };
  }, [token, notifOpen]);

  const formatNotificationDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString();
  };

  const navigationLabel = t("header.nav.ariaLabel", "Main navigation");
  const userLabel =
    displayName || userEmail || t("header.nav.profile", "Account");

  // Baskets lead: they are what the marketplace sells. Farms follow, because a
  // farm is how a shopper decides which baskets to trust. The standalone product
  // catalogue is deliberately not here — a product is an ingredient of a basket,
  // reachable through the farm that grows it.
  const navItems = [
    {
      key: "baskets",
      label: t("nav.baskets", { defaultValue: "Baskets" }),
      onClick: () => navigate("/baskets"),
    },
    {
      key: "farms",
      label: t("header.nav.farms", { defaultValue: "Farms" }),
      onClick: () => navigate("/farms"),
    },
    {
      key: "about",
      label: t("header.nav.about"),
      onClick: () => handleNavigation("about"),
    },
    {
      key: "contact",
      label: t("header.nav.contact"),
      onClick: () => handleNavigation("contact"),
    },
    {
      key: "profile",
      label: t("header.nav.profile"),
      onClick: () => navigate(accountHomeRoute),
    },
  ];

  return (
    <>
      {!isMobile ? (
        <>
        <S.Container $scrolled={scrolled} aria-label={navigationLabel}>
          <S.LogoButton type="button" onClick={handleHomeClick}>
            <S.LogoImage src={logo} alt={t("header.logo")} />
            <S.LogoText>{t("header.logo")}</S.LogoText>
          </S.LogoButton>
          <S.NavLinks>
            {navItems.map((item) => (
              <UIButton
                key={item.key}
                variant="ghost"
                size="sm"
                onClick={item.onClick}
              >
                {item.label}
              </UIButton>
            ))}
          </S.NavLinks>
          <S.RightGroup>
            {isFarmer ? (
              <S.FarmButton type="button" onClick={goToFarm} aria-label={farmAria}>
                <FaLeaf aria-hidden="true" />
                {farmLabel}
                {taskCount > 0 ? (
                  <S.FarmCount aria-hidden="true">
                    {taskCount > 99 ? "99+" : taskCount}
                  </S.FarmCount>
                ) : null}
              </S.FarmButton>
            ) : null}
            <LanguageSwitcher />
            {token ? (
              <S.NotificationWrap ref={notifRef}>
                <S.IconButton
                  type="button"
                  onClick={() => {
                    setNotifOpen((open) => !open);
                    setMenuOpen(false);
                  }}
                  aria-label={t("header.nav.notifications", "Notifications")}
                  aria-haspopup="menu"
                  aria-expanded={notifOpen}
                >
                  <LuBell />
                </S.IconButton>
                {unreadCount > 0 ? (
                  <S.NotificationBadge>
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </S.NotificationBadge>
                ) : null}
                {notifOpen ? (
                  <S.NotificationDropdown role="menu">
                    <S.NotificationHeader>
                      <S.NotificationTitle>
                        {t("notifications.title", "Notifications")}
                      </S.NotificationTitle>
                      <S.NotificationActions>
                        <S.NotificationAction
                          type="button"
                          onClick={async () => {
                            try {
                              await markAllNotificationsRead(token);
                              setNotifications((prev) =>
                                prev.map((n) => ({ ...n, isRead: true }))
                              );
                              setUnreadCount(0);
                            } catch (err) {
                              console.error(err);
                            }
                          }}
                          disabled={
                            notifications.length === 0 ||
                            notifications.every((n) => n.isRead)
                          }
                        >
                          {t("notifications.mark_all", "Mark all read")}
                        </S.NotificationAction>
                        <S.NotificationAction
                          type="button"
                          onClick={() => {
                            setNotifOpen(false);
                            navigate(accountHomeRoute);
                          }}
                        >
                          {t("notifications.view_all", "View all")}
                        </S.NotificationAction>
                      </S.NotificationActions>
                    </S.NotificationHeader>
                    {notifLoading ? (
                      <S.NotificationEmpty>
                        {t("notifications.loading", "Loading...")}
                      </S.NotificationEmpty>
                    ) : notifications.length === 0 ? (
                      <S.NotificationEmpty>
                        {t("notifications.empty", "No notifications yet.")}
                      </S.NotificationEmpty>
                    ) : (
                      <S.NotificationList>
                        {notifications.map((note) => (
                          <S.NotificationItem
                            key={note.id}
                            $unread={!note.isRead}
                          >
                            <S.NotificationMessage>
                              {note.message || note.title}
                            </S.NotificationMessage>
                            <S.NotificationMeta>
                              <span>{formatNotificationDate(note.createdUtc)}</span>
                              {!note.isRead ? (
                                <S.NotificationAction
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      await markNotificationRead(token, note.id);
                                      setNotifications((prev) =>
                                        prev.map((n) =>
                                          n.id === note.id
                                            ? { ...n, isRead: true }
                                            : n
                                        )
                                      );
                                      setUnreadCount((count) =>
                                        Math.max(0, count - 1)
                                      );
                                    } catch (err) {
                                      console.error(err);
                                    }
                                  }}
                                >
                                  {t("notifications.mark_read", "Mark read")}
                                </S.NotificationAction>
                              ) : null}
                            </S.NotificationMeta>
                          </S.NotificationItem>
                        ))}
                      </S.NotificationList>
                    )}
                  </S.NotificationDropdown>
                ) : null}
              </S.NotificationWrap>
            ) : null}
            <S.NotificationWrap>
              <S.IconButton
                type="button"
                onClick={openDrawer}
                aria-label={t("cart.open_cart", "Open cart") + (cartCount > 0 ? `, ${t("cart.badge_aria", "Cart: {{count}} items", { count: cartCount })}` : "")}
              >
                <FaShoppingCart />
              </S.IconButton>
              {cartCount > 0 && (
                <S.NotificationBadge key={cartCount}>
                  {cartCount > 99 ? "99+" : cartCount}
                </S.NotificationBadge>
              )}
            </S.NotificationWrap>
            <S.IconButton
              type="button"
              onClick={toggleTheme}
              aria-label={t("header.nav.themeToggle", "Toggle theme")}
            >
              {theme === "dark" ? <LuSunMedium /> : <LuMoonStar />}
            </S.IconButton>
            {token ? (
              <S.UserMenu ref={menuRef}>
                <S.UserButton
                  variant="outline"
                  size="sm"
                  onClick={() => setMenuOpen((open) => !open)}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                >
                  <FiUser aria-hidden="true" />
                      <S.UserName>{userLabel}</S.UserName>
                </S.UserButton>
                {menuOpen ? (
                  <S.UserDropdown role="menu">
                    <S.DropdownItem
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        navigate(accountHomeRoute);
                      }}
                    >
                      {isFarmer ? farmLabel : t("header.nav.profile") || "Profile"}
                      {isFarmer && taskCount > 0 ? (
                        <S.FarmCount aria-hidden="true">
                          {taskCount > 99 ? "99+" : taskCount}
                        </S.FarmCount>
                      ) : null}
                    </S.DropdownItem>
                    <S.DropdownItem
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        navigate("/account");
                      }}
                    >
                      {t("account.title", { defaultValue: "Account settings" })}
                    </S.DropdownItem>
                    {userRole === "SuperAdmin" ? (
                      <S.DropdownItem
                        type="button"
                        onClick={() => {
                          setMenuOpen(false);
                          navigate("/admin");
                        }}
                      >
                        {t("admin.sidebar.admin", "Admin")}
                      </S.DropdownItem>
                    ) : null}
                    <S.DropdownItem
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        openDrawer();
                      }}
                    >
                      {t("cart.page_title", "Cart")}
                    </S.DropdownItem>
                    <S.DangerItem
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        logout();
                        navigate("/login");
                      }}
                    >
                      {t("button.logout", "Logout")}
                    </S.DangerItem>
                  </S.UserDropdown>
                ) : null}
              </S.UserMenu>
            ) : (
              <S.LoginActions>
                <AuthMenuButton
                  onLoginClick={() => handleButtonClick("login")}
                  onSignupClick={() => handleButtonClick("signup")}
                />
              </S.LoginActions>
            )}
          </S.RightGroup>
        </S.Container>
        <S.NavSpacer />
        </>
      ) : (
        <>
          <S.TopBar $scrolled={scrolled} aria-label={navigationLabel}>
            <S.LogoButton type="button" onClick={handleHomeClick}>
              <S.LogoImage src={logo} alt={t("header.logo")} />
              <S.LogoText>{t("header.logo")}</S.LogoText>
            </S.LogoButton>
            <S.MobileControls>
              <LanguageSwitcher />
              {token ? (
                <S.NotificationWrap ref={notifRef}>
                  <S.IconButton
                    type="button"
                    onClick={() => {
                      setNotifOpen((open) => !open);
                      setMenuOpen(false);
                    }}
                    aria-label={t("header.nav.notifications", "Notifications")}
                    aria-haspopup="menu"
                    aria-expanded={notifOpen}
                  >
                    <LuBell />
                  </S.IconButton>
                  {unreadCount > 0 ? (
                    <S.NotificationBadge>
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </S.NotificationBadge>
                  ) : null}
                  {notifOpen ? (
                    <S.NotificationDropdown role="menu">
                      <S.NotificationHeader>
                        <S.NotificationTitle>
                          {t("notifications.title", "Notifications")}
                        </S.NotificationTitle>
                        <S.NotificationActions>
                          <S.NotificationAction
                            type="button"
                            onClick={async () => {
                              try {
                                await markAllNotificationsRead(token);
                                setNotifications((prev) =>
                                  prev.map((n) => ({ ...n, isRead: true }))
                                );
                                setUnreadCount(0);
                              } catch (err) {
                                console.error(err);
                              }
                            }}
                            disabled={
                              notifications.length === 0 ||
                              notifications.every((n) => n.isRead)
                            }
                          >
                            {t("notifications.mark_all", "Mark all read")}
                          </S.NotificationAction>
                          <S.NotificationAction
                            type="button"
                            onClick={() => {
                              setNotifOpen(false);
                              navigate(accountHomeRoute);
                            }}
                          >
                            {t("notifications.view_all", "View all")}
                          </S.NotificationAction>
                        </S.NotificationActions>
                      </S.NotificationHeader>
                      {notifLoading ? (
                        <S.NotificationEmpty>
                          {t("notifications.loading", "Loading...")}
                        </S.NotificationEmpty>
                      ) : notifications.length === 0 ? (
                        <S.NotificationEmpty>
                          {t("notifications.empty", "No notifications yet.")}
                        </S.NotificationEmpty>
                      ) : (
                        <S.NotificationList>
                          {notifications.map((note) => (
                            <S.NotificationItem
                              key={note.id}
                              $unread={!note.isRead}
                            >
                              <S.NotificationMessage>
                                {note.message || note.title}
                              </S.NotificationMessage>
                              <S.NotificationMeta>
                                <span>{formatNotificationDate(note.createdUtc)}</span>
                                {!note.isRead ? (
                                  <S.NotificationAction
                                    type="button"
                                    onClick={async () => {
                                      try {
                                        await markNotificationRead(token, note.id);
                                        setNotifications((prev) =>
                                          prev.map((n) =>
                                            n.id === note.id
                                              ? { ...n, isRead: true }
                                              : n
                                          )
                                        );
                                        setUnreadCount((count) =>
                                          Math.max(0, count - 1)
                                        );
                                      } catch (err) {
                                        console.error(err);
                                      }
                                    }}
                                  >
                                    {t("notifications.mark_read", "Mark read")}
                                  </S.NotificationAction>
                                ) : null}
                              </S.NotificationMeta>
                            </S.NotificationItem>
                          ))}
                        </S.NotificationList>
                      )}
                    </S.NotificationDropdown>
                  ) : null}
                </S.NotificationWrap>
              ) : null}
              <S.IconButton
                type="button"
                onClick={toggleTheme}
                aria-label={t("header.nav.themeToggle", "Toggle theme")}
              >
                {theme === "dark" ? <LuSunMedium /> : <LuMoonStar />}
              </S.IconButton>
              <S.AuthAction>
                {token ? (
                  <S.UserMenu ref={menuRef}>
                    <S.UserButton
                      aria-label={userLabel}
                      variant="outline"
                      size="sm"
                      onClick={() => setMenuOpen((open) => !open)}
                      aria-haspopup="menu"
                      aria-expanded={menuOpen}
                    >
                      <FiUser aria-hidden="true" />
                      <S.UserName>{userLabel}</S.UserName>
                    </S.UserButton>
                    {menuOpen ? (
                      <S.UserDropdown role="menu">
                        <S.DropdownItem
                          type="button"
                          onClick={() => {
                            setMenuOpen(false);
                            navigate(accountHomeRoute);
                          }}
                        >
                          {isFarmer ? farmLabel : t("header.nav.profile") || "Profile"}
                      {isFarmer && taskCount > 0 ? (
                        <S.FarmCount aria-hidden="true">
                          {taskCount > 99 ? "99+" : taskCount}
                        </S.FarmCount>
                      ) : null}
                        </S.DropdownItem>
                        <S.DropdownItem
                          type="button"
                          onClick={() => {
                            setMenuOpen(false);
                            navigate("/account");
                          }}
                        >
                          {t("account.title", { defaultValue: "Account settings" })}
                        </S.DropdownItem>
                        {userRole === "SuperAdmin" ? (
                          <S.DropdownItem
                            type="button"
                            onClick={() => {
                              setMenuOpen(false);
                              navigate("/admin");
                            }}
                          >
                            {t("admin.sidebar.admin", "Admin")}
                          </S.DropdownItem>
                        ) : null}
                        <S.DropdownItem
                          type="button"
                          onClick={() => {
                            setMenuOpen(false);
                            openDrawer();
                          }}
                        >
                          {t("cart.page_title", "Cart")}
                        </S.DropdownItem>
                        <S.DangerItem
                          type="button"
                          onClick={() => {
                            setMenuOpen(false);
                            logout();
                            navigate("/login");
                          }}
                        >
                          {t("button.logout", "Logout")}
                        </S.DangerItem>
                      </S.UserDropdown>
                    ) : null}
                  </S.UserMenu>
                ) : (
                  <S.LogoutBtn
                    variant="primary"
                    size="sm"
                    onClick={() => handleButtonClick("login")}
                  >
                    {t("button.login")}
                  </S.LogoutBtn>
                )}
              </S.AuthAction>
            </S.MobileControls>
          </S.TopBar>
          {/* A farmer's portal now has its own tab here, so Profile goes where the
              label promises instead of landing on the same screen twice. */}
          <BottomNavbar
            isLoggedIn={Boolean(token)}
            onHomeClick={handleHomeClick}
            onFarmsClick={() => navigate("/farms")}
            onBasketsClick={() => navigate("/baskets")}
            onLoginClick={
              token
                ? null
                : () => handleButtonClick("login")
            }
            onProfileClick={
              token ? () => navigate(isFarmer ? "/account" : accountHomeRoute) : null
            }
            onFarmPanelClick={isFarmer ? () => navigate("/villager") : null}
            farmTaskCount={taskCount}
          />
        </>
      )}
    </>
  );
};

export default Navbar;
